import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Star } from 'lucide-react';
import { haptic } from '../../utils/haptics';

/**
 * StarRating — fuenf Sterne als EINE Auswahl (Session-Feedback, Logbucheintrag).
 *
 * Bis September 2026 hatten beide Formulare je eine eigene Kopie: fuenf Knoepfe
 * mit `p-2` + `gap-2` um 28-32px-Sterne — zusammen gut 270px breit, mit viel
 * Luft zwischen den Sternen. Hier sitzt jeder Stern (24px) in einem 44px-Feld
 * (Tipp-Ziel, MOBILE_UX_GUIDE.md) ohne Abstand zum Nachbarn: die Reihe ist
 * 220px breit, die Tipp-Flaeche lueckenlos, und beide Formulare sehen gleich aus.
 *
 * Auszeichnung wie ChoiceGroup: `role="radiogroup"` + `role="radio"` je Stern,
 * Roving Tabindex (ein Tab-Stopp, Pfeiltasten wechseln den Wert). Ohne das liest
 * ein Screenreader fuenf namenlose Schaltflaechen ohne Hinweis auf die Skala.
 *
 * `align="end"` zieht den Rand des letzten Feldes nach rechts aus, damit der
 * Stern selbst buendig mit dem Textrand steht — fuer die Zeile "Frage links,
 * Sterne rechts", die beide Formulare nutzen.
 */
// Haptik (Oktober 2026): `haptic.selection()` bei einem echten Wertwechsel,
// ausgelöst hier im Baustein; Aufrufer ergänzen keine. Optional verkettet,
// weil Tests `utils/haptics` oft nur teilweise mocken.
const tick = () => haptic?.selection?.();

const STARS = [1, 2, 3, 4, 5];

const StarRating = ({
  value,
  onChange,
  label,
  labelledBy,
  allowClear = false,
  align = 'start',
  className = '',
}) => {
  const containerRef = useRef(null);
  const current = Number(value) || 0;

  const select = (next) => {
    if (next !== current) tick();
    onChange(next);
    containerRef.current?.querySelectorAll('[role="radio"]')[next - 1]?.focus();
  };

  const handleKeyDown = (event) => {
    const up = event.key === 'ArrowRight' || event.key === 'ArrowUp';
    const down = event.key === 'ArrowLeft' || event.key === 'ArrowDown';
    if (!up && !down) return;
    event.preventDefault();
    if (up) select(Math.min(STARS.length, current + 1));
    else select(Math.max(1, current - 1));
  };

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      onKeyDown={handleKeyDown}
      className={[
        'flex',
        align === 'end' ? '-mr-2.5' : '-ml-2.5',
        className,
      ].filter(Boolean).join(' ')}
    >
      {STARS.map((star) => {
        const selected = current === star;
        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`${star} von 5 Sternen`}
            tabIndex={selected || (current === 0 && star === 1) ? 0 : -1}
            onClick={() => {
              const next = allowClear && selected ? 0 : star;
              if (next !== current) tick();
              onChange(next);
            }}
            className={[
              'flex h-11 w-11 items-center justify-center rounded-lg transition-colors duration-base',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              current >= star ? 'text-warning' : 'text-muted-foreground/30 hover:text-muted-foreground/60',
            ].join(' ')}
          >
            <Star className="h-6 w-6 fill-current" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
};

StarRating.propTypes = {
  /** 0 = nichts gewaehlt, sonst 1-5 */
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  /** Accessible Name der Gruppe — nur noetig, wenn kein `labelledBy` gesetzt ist */
  label: PropTypes.string,
  /** id eines sichtbaren Textes, der die Gruppe beschriftet */
  labelledBy: PropTypes.string,
  /** Nochmaliges Tippen auf den gewaehlten Stern hebt die Wahl auf (optionale Bewertung) */
  allowClear: PropTypes.bool,
  align: PropTypes.oneOf(['start', 'end']),
  className: PropTypes.string,
};

export default StarRating;
